(() => {
  if (customElements.get('as-shipping-estimator')) return;
  const CREATE = 'mutation QuoteCart($input: CartInput!) { cartCreate(input: $input) { cart { id totalQuantity } userErrors { field message code } } }';
  const RATES = 'query ShippingQuote($id: ID!) { cart(id: $id) { ... @defer { deliveryGroups(first: 10, withCarrierRates: true) { nodes { id deliveryOptions { title description handle deliveryMethodType estimatedCost { amount currencyCode } } } } } } }';
  const ADDRESS = 'mutation QuoteAddress($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) { cartDeliveryAddressesReplace(cartId: $cartId, addresses: $addresses) { cart { id } userErrors { code field message } } }';
  const SELECT = 'mutation QuoteMethod($cartId: ID!, $options: [CartSelectedDeliveryOptionInput!]!) { cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $options) { cart { id } userErrors { code field message } } }';
  const PROVINCES = { ACT:'Australian Capital Territory', NSW:'New South Wales', NT:'Northern Territory', QLD:'Queensland', SA:'South Australia', TAS:'Tasmania', VIC:'Victoria', WA:'Western Australia' };
  function parseResponse(body, contentType) {
    if (!contentType.includes('multipart/mixed')) return [JSON.parse(body)];
    const boundary = contentType.match(/boundary=(?:"([^"]+)"|([^;\s]+))/i);
    if (!boundary) throw new Error('Incomplete shipping response');
    return body.split('--' + (boundary[1] || boundary[2])).flatMap(part => {
      const start = part.indexOf('{');
      const end = part.lastIndexOf('}');
      return start < 0 || end < start ? [] : [JSON.parse(part.slice(start, end + 1))];
    });
  }
  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.status = this.querySelector('.as-shipping__status');
      this.results = this.querySelector('.as-shipping__results');
      this.text = JSON.parse(this.querySelector('[data-shipping-text]').textContent);
      this.listeners = new AbortController();
      this.setupLocationSearch();
      this.form.addEventListener('submit', event => { event.preventDefault(); this.calculate(); }, {signal:this.listeners.signal});
      this.form.addEventListener('input', () => this.invalidate(), {signal:this.listeners.signal});
      this.form.addEventListener('change', () => this.invalidate(), {signal:this.listeners.signal});
      const scope = this.closest('product-info') || this.closest('.shopify-section') || document;
      scope.addEventListener('change', event => { if (!this.contains(event.target)) this.invalidate(); }, {signal:this.listeners.signal});
      scope.addEventListener('input', event => { if (!this.contains(event.target) && event.target.name === 'quantity') this.invalidate(); }, {signal:this.listeners.signal});
    }
    disconnectedCallback() {
      this.request?.abort();
      this.listeners?.abort();
      this.ready = false;
    }
    setupLocationSearch() {
      this.locationInput = this.querySelector('.as-shipping__location');
      this.placeList = this.querySelector('.as-shipping__places');
      this.placeHelp = this.querySelector('.as-shipping__search-help');
      this.clearLocation = this.querySelector('.as-shipping__clear');
      this.manualButton = this.querySelector('.as-shipping__manual');
      const opts = {signal:this.listeners.signal};
      this.locationInput.addEventListener('input', () => {
        this.chosenLocation = null;
        for (const name of ['postcode','suburb','state']) this.form.elements.namedItem(name).value = '';
        this.locationInput.setCustomValidity('');
        this.clearLocation.hidden = !this.locationInput.value;
        this.searchLocations();
      }, opts);
      this.locationInput.addEventListener('focus', () => { if (!this.chosenLocation) this.searchLocations(); }, opts);
      this.locationInput.addEventListener('keydown', event => {
        if (event.key === 'Escape') { this.closePlaces(); return; }
        if (this.placeList.hidden || !this.matches?.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.activePlace = this.activePlace < 0 ? (step > 0 ? 0 : this.matches.length - 1) : (this.activePlace + step + this.matches.length) % this.matches.length;
          [...this.placeList.children].forEach((el,i) => el.setAttribute('aria-selected',String(i === this.activePlace)));
          const active = this.placeList.children[this.activePlace];
          this.locationInput.setAttribute('aria-activedescendant',active.id);
          active.scrollIntoView({block:'nearest'});
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (this.activePlace >= 0) this.choosePlace(this.matches[this.activePlace]);
          else this.placeHelp.textContent = 'Choose a suburb from the list below.';
        }
      }, opts);
      this.placeList.addEventListener('pointerdown', event => event.preventDefault(), opts);
      this.placeList.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (item) this.choosePlace(this.matches[Number(item.dataset.index)]);
      }, opts);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.closePlaces(); }, opts);
      this.clearLocation.addEventListener('click', () => {
        this.locationInput.value = '';
        this.locationInput.dispatchEvent(new Event('input',{bubbles:true}));
        this.locationInput.focus();
      }, opts);
      this.manualButton.addEventListener('click', () => {
        this.manualLocation = true;this.chosenLocation = null;this.closePlaces();this.invalidate();
        this.querySelector('.as-shipping__search').hidden = true;
        this.locationInput.required = false;this.locationInput.setCustomValidity('');
        this.querySelector('.as-shipping__fields').hidden = false;
        for (const name of ['postcode','suburb','state']) this.form.elements.namedItem(name).required = true;
        this.form.elements.namedItem('postcode').focus();
      }, opts);
    }
    closePlaces() {
      this.placeList.hidden = true;
      this.locationInput.setAttribute('aria-expanded','false');
      this.locationInput.removeAttribute('aria-activedescendant');
      this.activePlace = -1;
    }
    async searchLocations() {
      const query = this.locationInput.value.trim().toLowerCase();
      this.closePlaces();
      if (query.length < 2) { this.placeHelp.textContent = 'Enter at least 2 letters or digits.';return; }
      this.placeHelp.textContent = 'Finding suburbs…';
      try {
        if (!ShippingEstimator.places) ShippingEstimator.places = fetch(this.dataset.postcodes,{credentials:'omit'})
          .then(response => { if (!response.ok) throw new Error('places');return response.json(); })
          .then(data => data.places)
          .catch(error => { ShippingEstimator.places = null;throw error; });
        const places = await ShippingEstimator.places;
        if (!this.isConnected || query !== this.locationInput.value.trim().toLowerCase() || this.chosenLocation || this.manualLocation) return;
        const words = query.split(/\s+/);
        const found = places.filter(p => words.every(w => (p[1]+' '+p[2]+' '+p[0]).toLowerCase().includes(w)))
          .sort((a,b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]));
        this.matches = found.slice(0,50);
        this.placeList.replaceChildren();
        this.matches.forEach((place,index) => {
          const item = document.createElement('li');item.setAttribute('role','option');item.setAttribute('aria-selected','false');
          item.id = this.placeList.id + '-' + index;item.dataset.index = String(index);
          item.textContent = this.placeLabel(place);this.placeList.append(item);
        });
        this.placeList.hidden = !found.length;
        this.locationInput.setAttribute('aria-expanded',String(!!found.length));
        this.placeHelp.textContent = found.length ? (found.length > 50 ? 'Showing 50 matches. Keep typing to narrow your search.' : found.length + ' suburbs found. Choose your delivery suburb.') : 'No matching suburb found. Try another search or enter details manually.';
        this.manualButton.hidden = false;
      } catch {
        if (query !== this.locationInput.value.trim().toLowerCase()) return;
        this.placeHelp.textContent = 'Suburb search is unavailable. Please enter your delivery details manually.';
        this.manualButton.hidden = false;
      }
    }
    placeLabel(place) {
      return place[1].toLowerCase().replace(/\b[a-z]/g,letter => letter.toUpperCase()) + ' ' + place[2] + ' ' + place[0];
    }
    choosePlace(place) {
      if (!place) return;
      this.invalidate();this.chosenLocation = place;
      this.form.elements.namedItem('postcode').value = place[0];
      this.form.elements.namedItem('suburb').value = place[1];
      this.form.elements.namedItem('state').value = place[2];
      this.locationInput.value = this.placeLabel(place);this.locationInput.setCustomValidity('');
      this.clearLocation.hidden = false;this.closePlaces();
      this.placeHelp.textContent = 'Delivery location selected.';this.manualButton.hidden = true;
      this.calculate();
    }
    invalidate() {
      this.request?.abort();
      this.request = null;
      this.button.disabled = false;
      this.button.textContent = this.text.calculate;
      this.button.hidden = !this.manualLocation && !this.chosenLocation;
      this.removeAttribute('aria-busy');
      if (this.results.childElementCount || this.status.textContent) this.status.textContent = this.text.changed;
      this.results.replaceChildren();
    }
    selection() {
      const productForm = document.getElementById(this.dataset.productForm);
      const idInput = productForm?.elements.namedItem('id');
      if (!productForm || !idInput || idInput.disabled || !/^\d+$/.test(idInput.value)) throw new Error('selection');
      const data = new FormData(productForm);
      const quantity = Number(data.get('quantity') || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 250) throw new Error('selection');
      const fields = new FormData(this.form);
      const postcode = String(fields.get('postcode') || '').trim();
      const suburb = String(fields.get('suburb') || '').trim();
      const state = String(fields.get('state') || '');
      if (!/^\d{4}$/.test(postcode) || !suburb || !PROVINCES[state]) throw new Error('selection');
      const line = {merchandiseId:'gid://shopify/ProductVariant/' + idInput.value, quantity};
      if (data.get('selling_plan')) line.sellingPlanId = 'gid://shopify/SellingPlan/' + data.get('selling_plan');
      return {line, postcode, suburb, state};
    }
    async send(query, variables, signal) {
      const response = await fetch(this.dataset.endpoint, {
        method:'POST', credentials:'omit', signal,
        headers:{'Content-Type':'application/json',Accept:'multipart/mixed; deferSpec=20220824, application/json'},
        body:JSON.stringify({query,variables})
      });
      if (!response.ok) throw new Error('service');
      const chunks = parseResponse(await response.text(), response.headers.get('content-type') || '');
      const errors = chunks.flatMap(chunk => [...(chunk.errors || []), ...(chunk.incremental || []).flatMap(part => part.errors || [])]);
      if (errors.some(error => /timed out/i.test(error.message))) throw new Error('timeout');
      if (!chunks.length || errors.length) throw new Error('service');
      return chunks;
    }
    quoteAddress(selection) {
      return [{address:{deliveryAddress:{countryCode:'AU',provinceCode:selection.state,city:selection.suburb,zip:selection.postcode}},selected:true,oneTimeUse:true}];
    }
    async replaceQuoteAddress(cartId, selection, signal) {
      const chunks = await this.send(ADDRESS, {cartId,addresses:this.quoteAddress(selection)}, signal);
      const payload = chunks[0]?.data?.cartDeliveryAddressesReplace;
      if (payload?.userErrors?.length || !payload?.cart?.id) throw new Error('service');
    }
    async quoteRates(cartId, signal) {
      let chunks;
      for (let attempt = 0; attempt < 2; attempt++) {
        try { chunks = await this.send(RATES, {id:cartId}, signal);break; }
        catch (error) { if (signal.aborted || attempt || !['timeout','service'].includes(error.message)) throw error; }
      }
      const groups = chunks.flatMap(chunk => [chunk.data?.cart?.deliveryGroups?.nodes || [], ...(chunk.incremental || []).map(part => part.data?.deliveryGroups?.nodes || [])].flat());
      return Array.from(new Map(groups.map(group => [group.id,group])).values());
    }
    async completeLocalRates(cartId, localGroups, selection, signal) {
      await this.replaceQuoteAddress(cartId, {state:'VIC',suburb:'MELBOURNE',postcode:'3000'}, signal);
      let shippingGroups = await this.quoteRates(cartId, signal);
      for (let attempt = 0; attempt < 3; attempt++) {
        const choices = shippingGroups.map(group => {
          const option = group.deliveryOptions?.find(rate => rate.deliveryMethodType === 'SHIPPING');
          if (!option) throw new Error('service');
          return {deliveryGroupId:group.id,deliveryOptionHandle:option.handle};
        });
        if (!choices.length) throw new Error('service');
        const chunks = await this.send(SELECT, {cartId,options:choices}, signal);
        const payload = chunks[0]?.data?.cartSelectedDeliveryOptionsUpdate;
        if (payload?.cart?.id && !payload.userErrors?.length) break;
        if (attempt === 2 || !payload?.userErrors?.length || payload.userErrors.some(error => error.code !== 'PENDING_DELIVERY_GROUPS')) throw new Error('service');
        shippingGroups = await this.quoteRates(cartId, signal);
      }
      await this.replaceQuoteAddress(cartId, selection, signal);
      const actualGroups = await this.quoteRates(cartId, signal);
      if (actualGroups.length !== 1) throw new Error('service');
      const carrierOptions = actualGroups[0].deliveryOptions?.filter(option => option.deliveryMethodType === 'SHIPPING') || [];
      if (!carrierOptions.length) throw new Error('service');
      const options = [...localGroups[0].deliveryOptions, ...carrierOptions];
      return [{...actualGroups[0],deliveryOptions:Array.from(new Map(options.map(option => [option.handle,option])).values())}];
    }
    async calculate() {
      if (!this.manualLocation && !this.chosenLocation) this.locationInput.setCustomValidity('Choose your suburb from the suggestions.');
      if (this.button.disabled || !this.form.reportValidity()) return;
      let selection;
      try { selection = this.selection(); } catch { this.status.textContent = this.text.invalid; return; }
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 60000);
      this.button.disabled = true;
      this.button.hidden = !this.manualLocation;
      this.button.textContent = this.text.loading;
      this.setAttribute('aria-busy','true');
      this.status.textContent = this.text.loading;
      this.results.replaceChildren();
      try {
        const created = await this.send(CREATE, {input:{
          lines:[selection.line], buyerIdentity:{countryCode:'AU'},
          delivery:{addresses:[{address:{deliveryAddress:{countryCode:'AU',provinceCode:selection.state,city:selection.suburb,zip:selection.postcode}},selected:true,oneTimeUse:true}]}
        }}, request.signal);
        const payload = created[0]?.data?.cartCreate;
        if (payload?.userErrors?.length || !payload?.cart?.id || payload.cart.totalQuantity !== selection.line.quantity) throw new Error('selection');
        let uniqueGroups = await this.quoteRates(payload.cart.id, request.signal);
        let partialRates = false;
        if (uniqueGroups.length === 1 && uniqueGroups[0].deliveryOptions?.length && uniqueGroups[0].deliveryOptions.every(option => option.deliveryMethodType === 'LOCAL')) {
          try { uniqueGroups = await this.completeLocalRates(payload.cart.id, uniqueGroups, selection, request.signal); }
          catch (error) { if (request.signal.aborted) throw error;partialRates = true; }
        }
        if (this.request !== request || !this.isConnected) return;
        if (JSON.stringify(selection) !== JSON.stringify(this.selection())) { this.invalidate(); return; }
        if (!uniqueGroups.length || uniqueGroups.some(group => !group.deliveryOptions?.length)) {
          this.status.textContent = this.text.unavailable;
        } else {
          this.render(uniqueGroups,selection);
        }
        if (partialRates || !uniqueGroups.length || uniqueGroups.some(group => !group.deliveryOptions?.length)) this.renderCartButton();
      } catch (error) {
        if (this.request === request) this.status.textContent = error.message === 'selection' ? this.text.invalid : this.text.error;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.button.disabled = false;
          this.button.textContent = this.text.calculate;
          this.button.hidden = !this.manualLocation && this.results.childElementCount > 0;
          this.removeAttribute('aria-busy');
          this.request = null;
        }
      }
    }
    renderCartButton() {
      const notice = document.createElement('p');
      notice.className = 'as-shipping__note';
      notice.textContent = this.text.partial_rates;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary as-shipping__submit';
      button.textContent = this.text.all_options;
      button.addEventListener('click', () => {
        const form = document.getElementById(this.dataset.productForm);
        const submit = form?.querySelector('button[type="submit"]');
        if (form && submit && !submit.disabled) form.requestSubmit(submit);
      }, {signal:this.listeners.signal});
      this.results.append(notice,button);
    }
    render(groups,selection) {
      this.status.textContent = this.text.found.replace('[quantity]',String(selection.line.quantity)).replace('[destination]',selection.suburb + ' ' + selection.postcode);
      if (groups.length > 1) {
        const split = document.createElement('p');split.textContent = this.text.split;this.results.append(split);
      }
      groups.forEach((group,index) => {
        if (groups.length > 1) { const heading = document.createElement('h4');heading.textContent = this.text.shipment + ' ' + (index + 1);this.results.append(heading); }
        const options = [...group.deliveryOptions].sort((a,b) => Number(a.estimatedCost.amount) - Number(b.estimatedCost.amount));
        const makeList = optionsToShow => {
          const list = document.createElement('ul');
          optionsToShow.forEach(option => {
          const amount = Number(option.estimatedCost.amount);
          if (!Number.isFinite(amount) || amount < 0) throw new Error('service');
          const row = document.createElement('li');row.className = 'as-shipping__rate';
          const title = document.createElement('span');title.className = 'as-shipping__rate-title';title.textContent = option.title;
          const price = document.createElement('span');price.className = 'as-shipping__price';price.textContent = new Intl.NumberFormat('en-AU',{style:'currency',currency:option.estimatedCost.currencyCode}).format(amount);
          const tax = document.createElement('span');tax.className = 'as-shipping__tax';tax.textContent = this.text.ex_gst;price.append(tax);
          row.append(title,price);
          if (option.description) { const description = document.createElement('span');description.className = 'as-shipping__description';description.textContent = option.description;row.append(description); }
          list.append(row);
          });
          return list;
        };
        this.results.append(makeList(options));
      });
    }
  }
  customElements.define('as-shipping-estimator', ShippingEstimator);
})();
